import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle2,
  Info,
  AlertTriangle,
  Wrench,
  List,
  X,
} from 'lucide-react';
import { SectionCard, StatusBadge } from './DashboardWidgets';
import { getKpiDefinition } from './kpiDefinitions';
import type { KpiDefinition } from './kpiDefinitions';

// ── Types ─────────────────────────────────────────────────────
interface TopFix {
  kpi: string;
  tool: string;
  action: string;
  location?: string;
  effort?: string;
  impact_points?: number;
  priority?: string;
}

export interface KpiDetailPanelProps {
  kpiId: string;
  kpiLabel: string;
  /** Normalised 0-100 health score — drives the bar and status colour. */
  value: unknown;
  /** Raw API value (e.g. actual count, bool, %) shown as "Current Value" and used
   *  in kpiDefinition cause/remediation templates. Falls back to `value` when absent. */
  rawValue?: unknown;
  topFixes: TopFix[];
  onClose: () => void;
}

// ── Status helpers ────────────────────────────────────────────
const KPI_STATUS_COLOR = { healthy: '#22c55e', warning: '#f59e0b', critical: '#ef4444' };

function scoreForValue(v: unknown): number | null {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 100 : 0;
  if (v === '100' || v === 'true') return 100;
  return null;
}

function statusForScore(score: number | null): 'healthy' | 'warning' | 'critical' {
  if (score == null) return 'critical';
  if (score >= 80) return 'healthy';
  if (score >= 60) return 'warning'; // matches card threshold (>= 60 amber, < 60 red)
  return 'critical';
}

/**
 * Format the actual metric value for human display in the "Current Value" row.
 * Boolean KPIs become "Pass"/"Fail" etc., percentages get a "%" suffix.
 */
function formatDisplayValue(kpiId: string, v: unknown): string {
  if (v == null) return 'N/A';
  // Boolean KPIs
  if (kpiId === 'sonar_gate_passed' || kpiId === 'image_scan_pass' || kpiId === 'pipeline_success') {
    return (v === true || v === 1 || v === 100) ? 'Pass' : 'Fail';
  }
  if (kpiId === 'branch_protected') return (v === true || v === 1 || v === 100) ? 'Protected' : 'Unprotected';
  // Percentage KPIs
  if (kpiId === 'sonar_coverage' || kpiId === 'sbom_coverage_pct' || kpiId === 'unreachable_cve_pct') {
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return isNaN(n) ? 'N/A' : `${n.toFixed(1)}%`;
  }
  if (typeof v === 'boolean') return v ? 'Pass' : 'Fail';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(1);
  return String(v);
}

// ── Component ─────────────────────────────────────────────────
export default function KpiDetailPanel({ kpiId, kpiLabel, value, rawValue, topFixes, onClose }: KpiDetailPanelProps) {
  const theme = useTheme();
  // Use raw value for cause/remediation text so templates see the actual count (e.g. 0 vulns), not the 0-100 score
  const effectiveRaw = rawValue !== undefined ? rawValue : value;
  const def: KpiDefinition | null = getKpiDefinition(kpiId, effectiveRaw);
  // Score bar always uses the normalised 0-100 health score
  const score = scoreForValue(value);
  const status = statusForScore(score);
  const statusColor = KPI_STATUS_COLOR[status];
  const filteredFixes = topFixes.filter((f) => f.kpi === kpiId);

  return (
    <SectionCard
      sx={{
        border: `2px solid ${alpha(statusColor, 0.3)}`,
        bgcolor: alpha(statusColor, 0.02),
        position: 'relative',
      }}
    >
      {/* Close button */}
      <Box
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.08) },
        }}
      >
        <X size={16} color={theme.palette.text.secondary} />
      </Box>

      {/* Header: KPI label + score bar + status */}
      <Stack spacing={1.5} sx={{ mb: 2, pr: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <Typography variant="subtitle1" fontWeight={700}>{kpiLabel}</Typography>
          {def && (
            <Chip
              label={def.tool}
              size="small"
              sx={{ height: 20, fontSize: '0.62rem', fontWeight: 600, bgcolor: alpha(theme.palette.info.main, 0.08), color: 'info.main' }}
            />
          )}
          <StatusBadge
            status={status === 'healthy' ? 'success' : status}
            label={status === 'healthy' ? 'Good' : status === 'warning' ? 'Warning' : 'Critical'}
          />
        </Stack>

        {/* Health score bar — 0–100 normalised score, not the raw count */}
        {score != null && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1, height: 8, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.06), overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.max(score, 0)}%`, height: '100%', bgcolor: statusColor, borderRadius: 2, transition: 'width 0.3s ease' }} />
            </Box>
            <Typography variant="body2" fontWeight={800} sx={{ color: statusColor, minWidth: 60, textAlign: 'right' }}>
              Score {score}/100
            </Typography>
          </Stack>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Body sections */}
      <Stack spacing={2.5}>
        {/* Current Value — shows actual metric (e.g. "0 vulns"), score bar above shows health */}
        <SectionRow
          icon={<Info size={14} />}
          title="Actual Value"
          color={statusColor}
        >
          <Typography variant="body1" fontWeight={700} sx={{ color: statusColor }}>
            {formatDisplayValue(kpiId, effectiveRaw)}
          </Typography>
        </SectionRow>

        {/* What is this? */}
        {def && (
          <SectionRow
            icon={<Info size={14} />}
            title="What is this?"
            color={theme.palette.info.main}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              {def.definition}
            </Typography>
          </SectionRow>
        )}

        {/* Why is this happening? */}
        {def && (
          <SectionRow
            icon={<AlertTriangle size={14} />}
            title="Why is this happening?"
            color={theme.palette.warning.main}
          >
            <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
              {def.cause}
            </Typography>
          </SectionRow>
        )}

        {/* Affected Items / Unavailable Hint */}
        {def?.itemsUnavailableHint && (
          <SectionRow
            icon={<List size={14} />}
            title="Affected Items"
            color={theme.palette.info.main}
          >
            <Alert severity="info" sx={{ py: 0.5, fontSize: '0.78rem' }}>{def.itemsUnavailableHint}</Alert>
          </SectionRow>
        )}

        {/* Remediation Plan */}
        <SectionRow
          icon={<Wrench size={14} />}
          title="Remediation Plan"
          color={theme.palette.error.main}
        >
          {(() => {
            const steps = def?.remediation || [];
            if (steps.length === 0 && filteredFixes.length === 0) {
              return <Alert severity="success" sx={{ py: 0.5 }}>No action required — this metric is healthy.</Alert>;
            }
            return (
              <Stack spacing={1}>
                {/* Static remediation from definition */}
                {steps.map((step, i) => (
                  <Box
                    key={`def-${i}`}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      p: 1.25,
                      borderRadius: 1,
                      borderLeft: `3px solid ${statusColor}`,
                      bgcolor: alpha(statusColor, 0.04),
                    }}
                  >
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{step}</Typography>
                  </Box>
                ))}

                {/* Top fixes from AI analysis */}
                {filteredFixes.map((fix, idx) => {
                  const sev = (fix.priority || 'medium').toLowerCase();
                  const sevColor = sev === 'critical' ? '#ef4444' : sev === 'high' ? '#f97316' : sev === 'medium' ? '#f59e0b' : '#3b82f6';
                  return (
                    <Box
                      key={`fix-${idx}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Chip
                          label={fix.priority || 'Medium'}
                          size="small"
                          sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: alpha(sevColor, 0.1), color: sevColor, borderRadius: 1 }}
                        />
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{fix.action}</Typography>
                      </Stack>
                      {fix.location && (
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}><b>Location:</b> <code>{fix.location}</code></Typography>
                      )}
                      {fix.effort && (
                        <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.5 }}>
                          Effort: {fix.effort}{fix.impact_points != null ? ` • Impact: ${fix.impact_points.toFixed(1)} pts` : ''}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            );
          })()}
        </SectionRow>
      </Stack>

      {/* Close link */}
      <Typography
        onClick={onClose}
        sx={{
          fontSize: 11,
          color: 'primary.main',
          fontWeight: 600,
          mt: 2.5,
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        ← Close details
      </Typography>
    </SectionCard>
  );
}

// ── Section Row ───────────────────────────────────────────────
function SectionRow({
  icon,
  title,
  color,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
        <Box sx={{ display: 'flex', color }}>{icon}</Box>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}
        >
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}
